// 处理 storage 的异常
// 参考 https://developer.mozilla.org/zh-CN/docs/Web/API/Web_Storage_API/Using_the_Web_Storage_API

import * as Sentry from '@sentry/react';

type StorageType = 'localStorage' | 'sessionStorage';

/**
 * 将同一类信息上报给sentry
 * 避免循环引用问题，冗余一份
 * @param param0 配置需要记录的信息
 */
function emitFingerprintSentry(opts: { message?: string; fingerprint: string; error?: Error; level?: string }) {
  try {
    // 第一次请求多语言失败了，上报一下成功的多语言
    Sentry.withScope(function setScope(scope) {
      scope.setLevel((opts?.level ?? 'debug') as Sentry.Severity);
      scope.setExtra('message', opts?.message);
      scope.setFingerprint([opts?.fingerprint ?? 'common']);
      // 手动捕获异常
      Sentry.captureException(opts?.error);
    });
  } catch (e) {
    // 无需再处理
  }
}

// 检查是否支持 type 类型的本地存储
function storageAvailable(type: StorageType) {
  let storage;
  try {
    storage = window[type];
    const x = '__storage_test__';
    storage.setItem(x, x);
    storage.removeItem(x);
    return true;
  } catch (e) {
    return (
      e instanceof DOMException &&
      // everything except Firefox
      (e.code === 22 ||
        // Firefox
        e.code === 1014 ||
        // test name field too, because code might not be present
        // everything except Firefox
        e.name === 'QuotaExceededError' ||
        // Firefox
        e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
      // acknowledge QuotaExceededError only if there's something already stored
      storage &&
      storage.length !== 0
    );
  }
}

/**
 * 自定义的存储对象，其实就是对本地存储做了一层包装，将调用都使用 try catch 包裹
 */
class CustomStorage implements Storage {
  private type: StorageType;

  private nativeStorageAvailable: boolean;

  private storage: Storage;

  constructor(storageType: StorageType) {
    this.nativeStorageAvailable = storageAvailable(storageType);
    this.type = storageType;
    this.storage = window[storageType];
  }

  @tryWrapperd(null)
  getItem(key: string): string | null {
    return this.storage.getItem(key);
  }

  @tryWrapperd()
  setItem(key: string, value: string): void {
    return this.storage.setItem(key, value);
  }

  @tryWrapperd()
  removeItem(key: string): void {
    return this.storage.removeItem(key);
  }

  @tryWrapperd(null)
  key(index: number): string | null {
    return this.storage.key(index);
  }

  @tryWrapperd()
  clear(): void {
    return this.storage.clear();
  }

  @tryWrapperd(0)
  get length(): number {
    return this.storage.length;
  }
}

// 装饰器，使用 try catch 处理异常
function tryWrapperd(defaultValue?: any) {
  return function tryCatchDecorator(target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const targetKey = descriptor.get ? 'get' : 'value';
    const method = descriptor[targetKey];
    function wrapperFn(this: any, ...args: any[]) {
      if (!this.nativeStorageAvailable) {
        emitFingerprintSentry({
          fingerprint: 'storage 操作异常',
          message: `当前不支持 ${this.type}`,
          error: new Error(`当前不支持 ${this.type}`),
        });
        return defaultValue;
      }

      let res = defaultValue;
      try {
        res = method.apply(this, args); // 调用之前的函数
      } catch (error) {
        emitFingerprintSentry({
          fingerprint: 'storage 操作异常',
          message: `当前不支持 ${this.type} ${propertyKey} ${args}`,
          error,
        });
      }

      return res;
    }

    // 这里好像只有直接替换会生效，Object.defineProperty 不会生效
    // https://www.tslang.cn/docs/handbook/decorators.html#decorator-factories
    // eslint-disable-next-line no-param-reassign
    descriptor[targetKey] = wrapperFn;
  };
}

const customLocalStorage = new CustomStorage('localStorage');
const customSessionStorage = new CustomStorage('sessionStorage');

export { customLocalStorage, customSessionStorage };
